/**
 * Profile Basics Step
 * 
 * Name and avatar collection (if not already set).
 * Issue #563 - First-Run Onboarding Flow
 */

import { useState } from 'react';
import type { Me } from '@/lib/api/auth';

interface ProfileBasicsStepProps {
  user: Me;
  onNext: () => void;
  onBack: () => void;
}

export default function ProfileBasicsStep({ user, onNext, onBack }: ProfileBasicsStepProps) {
  const [firstName, setFirstName] = useState(user.first_name || '');
  const [lastName, setLastName] = useState(user.last_name || '');

  // Skip if name already exists
  const hasName = user.first_name && user.last_name;
  if (hasName) {
    // Auto-advance if profile already complete
    setTimeout(() => onNext(), 0);
    return null;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Call API to update profile
    // For now, just advance
    onNext();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold text-[--oro-text-primary]">
          Tell us about yourself
        </h2>
        <p className="text-[--oro-text-secondary]">
          Help your community get to know you better.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label 
            htmlFor="firstName"
            className="block text-sm font-medium text-[--oro-text-primary] mb-1.5"
          >
            First Name
          </label>
          <input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg bg-[--oro-surface-secondary] border border-[--oro-border-primary] text-[--oro-text-primary] placeholder:text-[--oro-text-tertiary] focus:outline-none focus:ring-2 focus:ring-[--oro-accent-primary]"
            placeholder="Enter your first name"
            required
          />
        </div>

        <div>
          <label 
            htmlFor="lastName"
            className="block text-sm font-medium text-[--oro-text-primary] mb-1.5"
          >
            Last Name
          </label>
          <input
            id="lastName"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg bg-[--oro-surface-secondary] border border-[--oro-border-primary] text-[--oro-text-primary] placeholder:text-[--oro-text-tertiary] focus:outline-none focus:ring-2 focus:ring-[--oro-accent-primary]"
            placeholder="Enter your last name"
            required
          />
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2.5 rounded-lg bg-[--oro-surface-secondary] text-[--oro-text-primary] font-medium hover:bg-[--oro-surface-tertiary] transition-colors"
          >
            Back
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 rounded-lg bg-[--oro-accent-primary] text-[--oro-text-on-accent] font-medium hover:bg-[--oro-accent-hover] transition-colors"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}
